import {BasicReducerState, initialReducerState} from "../BasicReducerState";
import {IncomingInvoice} from "../../models/accounting/IncomingInvoice";
import {
    CREATE_INCOMING_INVOICE,
    CREATE_INCOMING_INVOICE_ERROR,
    CREATE_INCOMING_INVOICE_SUCCESS,
    DELETE_INCOMING_INVOICE_BY_ID,
    DELETE_INCOMING_INVOICE_BY_ID_ERROR,
    DELETE_INCOMING_INVOICE_BY_ID_SUCCESS,
    FETCH_INCOMING_INVOICE_BY_ID,
    FETCH_INCOMING_INVOICE_BY_ID_ERROR,
    FETCH_INCOMING_INVOICE_BY_ID_SUCCESS,
    FETCH_INCOMING_INVOICE_FILES_BY_ID,
    FETCH_INCOMING_INVOICE_FILES_BY_ID_ERROR,
    FETCH_INCOMING_INVOICE_FILES_BY_ID_SUCCESS,
    FETCH_INCOMING_INVOICES,
    FETCH_INCOMING_INVOICES_CHECK_FINAL,
    FETCH_INCOMING_INVOICES_CHECK_FINAL_ERROR,
    FETCH_INCOMING_INVOICES_CHECK_FINAL_SUCCESS,
    FETCH_INCOMING_INVOICES_ERROR,
    FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT,
    FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR,
    FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS,
    FETCH_INCOMING_INVOICES_SUCCESS,
    MARK_CANCELED_INCOMING_INVOICE,
    MARK_CANCELED_INCOMING_INVOICE_ERROR,
    MARK_CANCELED_INCOMING_INVOICE_SUCCESS,
    MARK_FINALIZED_INCOMING_INVOICE,
    MARK_FINALIZED_INCOMING_INVOICE_ERROR,
    MARK_FINALIZED_INCOMING_INVOICE_SUCCESS,
    IncomingInvoiceViewActionTypes,
    UPDATE_INCOMING_INVOICE,
    UPDATE_INCOMING_INVOICE_ERROR,
    UPDATE_INCOMING_INVOICE_SUCCESS
} from "../../actions/incoming-invoice/IncomingInvoiceActions";
import {
    DELETE_INCOMING_INVOICE_PAYMENT,
    DELETE_INCOMING_INVOICE_PAYMENT_ERROR,
    DELETE_INCOMING_INVOICE_PAYMENT_SUCCESS,
    INSERT_INCOMING_INVOICE_PAYMENT,
    INSERT_INCOMING_INVOICE_PAYMENT_ERROR,
    INSERT_INCOMING_INVOICE_PAYMENT_SUCCESS,
} from "../../actions/incoming-invoice/IncomingInvoicePaymentActions"
import {Nullable} from "../../models/common/Nullable";
import {
    errorFetchingRequestState,
    getInitialRequestState,
    RequestState,
    startFetchingRequestState,
    successFetchingRequestState
} from "../../models/common/RequestState";
import {Money} from "../../models/accounting/Finance";
import {Sensitive} from "../../models/common/Sensitive";
import {Identifier, makeNumericId} from "../../models/common/Identifier";
import {FileData} from "../../models/common/FileData";
import {
    FETCH_INCOMING_INVOICES_PROJECT_CONTRACTOR,
    FETCH_INCOMING_INVOICES_PROJECT_CONTRACTOR_ERROR,
    FETCH_INCOMING_INVOICES_PROJECT_CONTRACTOR_SUCCESS, IncomingInvoiceProjectContractorViewActionTypes
} from "../../actions/incoming-invoice/IncomingInvoiceProjectContractorActions";
import {RestoreQualificationAction} from "../../actions/qualifications/QualificationActions";
import {OptionType} from "../../components/form/SearchableSelect";


export interface IncomingInvoiceState extends BasicReducerState< Nullable< IncomingInvoice[] >, Nullable< IncomingInvoice >> {
    nextSerialState: string
    nextSerialRequest: RequestState
    prePaymentByProject: {
        projectId: Identifier<number>
        prePayment: Nullable< Sensitive< Money > >,
        request: RequestState
    }
    contractors: {
        request: RequestState,
        entries: OptionType[]
    }
    payment: {
        changingRequest: RequestState
    }
    files: {
        entries: { [ key: number ]: FileData[] }
        request: RequestState
    }
    checkFinalInvoice: {
        projectId: Identifier<number>
        finalInvoiceCount: number
        request: RequestState
    }
}

const initialState: IncomingInvoiceState = {
    ...initialReducerState(),
    nextSerialState: "0",
    nextSerialRequest: getInitialRequestState(),
    contractors: {
        request: getInitialRequestState(),
        entries: []
    },
    prePaymentByProject: {
        projectId: makeNumericId( 0),
        prePayment: null,
        request: getInitialRequestState()
    },
    files: {
        request: getInitialRequestState(),
        entries: {}
    },
    payment: {
        changingRequest: getInitialRequestState()
    },
    checkFinalInvoice: {
        projectId: makeNumericId( 0 ),
        finalInvoiceCount: 0,
        request: getInitialRequestState()
    }
};

export function IncomingInvoiceReducer(
    state: IncomingInvoiceState = initialState,
    action: IncomingInvoiceViewActionTypes | IncomingInvoiceProjectContractorViewActionTypes
) {

    switch( action.type ) {
        case FETCH_INCOMING_INVOICES:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: startFetchingRequestState()
                }
            };
        case FETCH_INCOMING_INVOICES_SUCCESS:
            return {
                ...state,
                totalEntries: action.payload.totalEntries,
                entries: {
                    current: action.payload.entries,
                    request: successFetchingRequestState()
                }
            };
        case FETCH_INCOMING_INVOICES_ERROR:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: errorFetchingRequestState( action.error )
                }
            };

        case FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT:
            return {
                ...state,
                prePaymentByProject: {
                    ...state.prePaymentByProject,
                    projectId: action.projectId,
                    request: startFetchingRequestState()
                }
            };
        case FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_SUCCESS:
            return {
                ...state,
                prePaymentByProject: {
                    ...state.prePaymentByProject,
                    prePayment: action.payload,
                    request: successFetchingRequestState()
                }
            };
        case FETCH_INCOMING_INVOICES_PRE_PAYMENT_BY_PROJECT_ERROR:
            return {
                ...state,
                prePaymentByProject: {
                    ...state.prePaymentByProject,
                    request: errorFetchingRequestState( action.error )
                }
            };
        case FETCH_INCOMING_INVOICES_CHECK_FINAL:
            return {
                ...state,
                checkFinalInvoice: {
                    finalInvoiceCount: 0,
                    projectId: action.projectId,
                    request: startFetchingRequestState()
                }
            };
        case FETCH_INCOMING_INVOICES_CHECK_FINAL_SUCCESS:
            return {
                ...state,
                checkFinalInvoice: {
                    ...state.checkFinalInvoice,
                    finalInvoiceCount: action.payload,
                    request: successFetchingRequestState()
                }
            };
        case FETCH_INCOMING_INVOICES_CHECK_FINAL_ERROR:
            return {
                ...state,
                checkFinalInvoice: {
                    ...state.checkFinalInvoice,
                    finalInvoiceCount: 0,
                    request: errorFetchingRequestState( action.error )
                }
            };
        case CREATE_INCOMING_INVOICE:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case CREATE_INCOMING_INVOICE_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case CREATE_INCOMING_INVOICE_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case FETCH_INCOMING_INVOICE_BY_ID:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [ action.invoiceId.rawValue ]: {
                        entry: null,
                        request: startFetchingRequestState()
                    }
                }
            };
        case FETCH_INCOMING_INVOICE_BY_ID_SUCCESS:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [ action.payload.invoiceId.rawValue ]: {
                        entry: action.payload,
                        request: successFetchingRequestState()
                    }
                }
            };
        case FETCH_INCOMING_INVOICE_BY_ID_ERROR:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [ action.invoiceId.rawValue ]: {
                        entry: null,
                        request: errorFetchingRequestState( action.error )
                    }
                }
            };
        case MARK_CANCELED_INCOMING_INVOICE:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case MARK_CANCELED_INCOMING_INVOICE_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case MARK_CANCELED_INCOMING_INVOICE_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case MARK_FINALIZED_INCOMING_INVOICE:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case MARK_FINALIZED_INCOMING_INVOICE_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case MARK_FINALIZED_INCOMING_INVOICE_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case INSERT_INCOMING_INVOICE_PAYMENT:
            return {
                ...state,
                payment: {
                    changingRequest: startFetchingRequestState()
                }
            };
        case INSERT_INCOMING_INVOICE_PAYMENT_SUCCESS:
            return {
                ...state,
                payment: {
                    changingRequest: successFetchingRequestState()
                }
            };
        case INSERT_INCOMING_INVOICE_PAYMENT_ERROR:
            return {
                ...state,
                payment: {
                    changingRequest: errorFetchingRequestState( action.error )
                }
            };
        case DELETE_INCOMING_INVOICE_PAYMENT:
            return {
                ...state,
                payment: {
                    changingRequest: startFetchingRequestState()
                }
            };
        case DELETE_INCOMING_INVOICE_PAYMENT_SUCCESS:
            return {
                ...state,
                payment: {
                    changingRequest: successFetchingRequestState()
                }
            };
        case DELETE_INCOMING_INVOICE_PAYMENT_ERROR:
            return {
                ...state,
                payment: {
                    changingRequest: errorFetchingRequestState( action.error )
                }
            };
        case FETCH_INCOMING_INVOICE_FILES_BY_ID:
            return {
                ...state,
                files: {
                    request: startFetchingRequestState(),
                    entries: { [ action.invoiceId.rawValue]: [] }
                }
            };
        case FETCH_INCOMING_INVOICE_FILES_BY_ID_SUCCESS:
            return {
                ...state,
                files: {
                    request: successFetchingRequestState(),
                    entries: { [ action.invoiceId.rawValue ]: action.payload }
                }
            };
        case FETCH_INCOMING_INVOICE_FILES_BY_ID_ERROR:
            return {
                ...state,
                files: {
                    request: errorFetchingRequestState( action.error )
                }
            };
        case UPDATE_INCOMING_INVOICE:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case DELETE_INCOMING_INVOICE_BY_ID:
            return {
                ...state,
                changingRequest: startFetchingRequestState()
            };
        case UPDATE_INCOMING_INVOICE_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case DELETE_INCOMING_INVOICE_BY_ID_SUCCESS:
            return {
                ...state,
                changingRequest: successFetchingRequestState()
            };
        case UPDATE_INCOMING_INVOICE_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case DELETE_INCOMING_INVOICE_BY_ID_ERROR:
            return {
                ...state,
                changingRequest: errorFetchingRequestState( action.error )
            };
        case FETCH_INCOMING_INVOICES_PROJECT_CONTRACTOR:
            return {
                ...state,
                contractors: {
                    request: startFetchingRequestState(),
                    entries: []
                }
            };
        case FETCH_INCOMING_INVOICES_PROJECT_CONTRACTOR_SUCCESS:
            return {
                ...state,
                contractors: {
                    request: successFetchingRequestState(),
                    entries: action.payload.entries
                }
            };
        case FETCH_INCOMING_INVOICES_PROJECT_CONTRACTOR_ERROR:
            return {
                ...state,
                contractors: {
                    request: errorFetchingRequestState( action.error ),
                    entries: []
                }
            };
        default:
            return state;
    }
}